import { useEffect, useState, type ReactNode } from "react";
import { Loading } from "@/components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { backOrigin, useBackTo } from "@/hooks/useBackTo";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";
import { ApiError } from "@/lib/apiClient";
import { toApiDateTime, dateOnly, formatDateTime } from "@/utils/dates";
import { toastApiError } from "@/lib/toast";
import { RECURRING_FREQUENCIES, RECURRING_STATUSES, recurringInvoices as api, type RecurringInvoiceDetail, type RecurringInvoiceItemInput, type SaveRecurringInvoiceRequest } from "@/features/quotes/api/quotes.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form } from "@/components/ui/form";
import { TextField, TextAreaField, SelectField, DateField, NumberField, NumberCell, CheckField, useAutoFocus, useUnsavedChangesWarning } from "@/components/form";
import { Notice } from "@/components/Notice";
import { useConfirm } from "@/components/ConfirmDialog";

const nonNegAmount = z.string().refine((s) => s === "" || (Number.isFinite(Number(s)) && Number(s) >= 0), "Must be ≥ 0.");
const wholeQty = z.string().refine((s) => s === "" || (Number.isInteger(Number(s)) && Number(s) >= 0), "Must be a whole number ≥ 0.");

const itemSchema = z.object({
  description: z.string().max(FIELD_MAX.recurringInvoice.itemDescription, tooLong("Item description", FIELD_MAX.recurringInvoice.itemDescription)),
  unitPrice: nonNegAmount,
  quantity: wholeQty,
});

const num = z.number().nullable();
const pct = z.number().min(0, "Value cannot be negative.").max(100, "Value cannot exceed 100.").nullable();

const schema = z.object({
  profileName: z.string().trim().min(1, "Profile name is required.").max(FIELD_MAX.recurringInvoice.profileName, tooLong("Profile name", FIELD_MAX.recurringInvoice.profileName)),
  frequency: z.string(),
  interval: num,
  startDate: z.string().min(1, "Start date is required."),
  endDate: z.string(),
  status: z.string(),
  emailSubject: z.string().max(FIELD_MAX.recurringInvoice.emailSubject, tooLong("Email subject", FIELD_MAX.recurringInvoice.emailSubject)),
  orderNumber: z.string().max(FIELD_MAX.recurringInvoice.orderNumber, tooLong("Order number", FIELD_MAX.recurringInvoice.orderNumber)),
  customerName: z.string().trim().min(1, "Customer name is required."),
  customerCompany: z.string(),
  customerEmail: z.string(),
  customerPhone: z.string(),
  customerAddress: z.string(),
  discountPct: pct,
  discountAmount: num,
  taxPct: pct,
  shippingCharge: num,
  discountAfterTax: z.boolean(),
  notes: z.string().max(FIELD_MAX.recurringInvoice.notes, tooLong("Notes", FIELD_MAX.recurringInvoice.notes)),
  items: z.array(itemSchema),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues["items"][number] = { description: "", unitPrice: "", quantity: "1" };

function blankValues(): FormValues {
  return {
    profileName: "",
    frequency: "Month",
    interval: 1,
    startDate: "",
    endDate: "",
    status: "Active",
    emailSubject: "",
    orderNumber: "",
    customerName: "",
    customerCompany: "",
    customerEmail: "",
    customerPhone: "",
    customerAddress: "",
    discountPct: 0,
    discountAmount: 0,
    taxPct: 0,
    shippingCharge: 0,
    discountAfterTax: false,
    notes: "",
    items: [{ ...EMPTY }],
  };
}

export default function RecurringInvoiceEditPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const backTo = useBackTo("/manage/recurring-invoices");
  const confirm = useConfirm();

  const [loaded, setLoaded] = useState<RecurringInvoiceDetail | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: blankValues(),
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: "items" });

  useAutoFocus(form, "profileName", !isEdit);

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    api.get(id)
      .then((r) => {
        if (cancelled) return;
        setLoaded(r);
        form.reset({
          profileName: r.profileName,
          frequency: r.frequency,
          interval: r.interval ?? 1,
          startDate: dateOnly(r.startDate),
          endDate: dateOnly(r.endDate),
          status: r.status,
          emailSubject: r.emailSubject ?? "",
          orderNumber: r.orderNumber ?? "",
          customerName: r.customerName,
          customerCompany: r.customerCompany ?? "",
          customerEmail: r.customerEmail ?? "",
          customerPhone: r.customerPhone ?? "",
          customerAddress: r.customerAddress ?? "",
          discountPct: (r.discountPct ?? 0) * 100,
          discountAmount: r.discountAmount ?? 0,
          taxPct: (r.taxRate ?? 0) * 100,
          shippingCharge: r.shippingCharge ?? 0,
          discountAfterTax: r.discountAppliesAfterTax ?? false,
          notes: r.notes ?? "",
          items:
            r.items.length > 0
              ? r.items.map((i) => ({
                  description: i.description,
                  unitPrice: String(i.unitPrice),
                  quantity: String(i.quantity),
                }))
              : [{ ...EMPTY }],
        });
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError(e instanceof Error ? e.message : "Failed to load.");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, isEdit, form]);

  function buildRequest(v: FormValues): SaveRecurringInvoiceRequest {
    const sanitisedItems: RecurringInvoiceItemInput[] = v.items
      .filter((it) => it.description.trim())
      .map((it) => ({
        description: it.description.trim(),
        unitPrice: toNum(it.unitPrice),
        quantity: Math.max(1, Math.floor(toNum(it.quantity)) || 1),
      }));
    return {
      profileName: v.profileName.trim(),
      frequency: v.frequency as SaveRecurringInvoiceRequest["frequency"],
      interval: Math.max(1, Math.floor(v.interval ?? 1) || 1),
      startDate: toApiDateTime(v.startDate) ?? "",
      endDate: toApiDateTime(v.endDate),
      status: v.status as SaveRecurringInvoiceRequest["status"],
      emailSubject: v.emailSubject.trim() || null,
      orderNumber: v.orderNumber.trim() || null,
      customerName: v.customerName.trim(),
      customerCompany: v.customerCompany.trim() || null,
      customerEmail: v.customerEmail.trim() || null,
      customerPhone: v.customerPhone.trim() || null,
      customerAddress: v.customerAddress.trim() || null,
      discountPct: (v.discountPct ?? 0) / 100,
      discountAmount: v.discountAmount ?? 0,
      taxRate: (v.taxPct ?? 0) / 100,
      shippingCharge: v.shippingCharge ?? 0,
      discountAppliesAfterTax: v.discountAfterTax,
      notes: v.notes.trim() || null,
      items: sanitisedItems,
    };
  }

  async function onValid(v: FormValues) {
    if (isEdit && !form.formState.isDirty) {
      toast.info("No changes to save");
      return;
    }
    if (v.items.some((it) => it.unitPrice.trim() !== "" && !it.description.trim())) {
      toast.error("Every line item needs a description.");
      return;
    }
    if (!v.items.some((it) => it.description.trim())) {
      toast.error("Add at least one line item.");
      return;
    }
    setError(null);
    try {
      const req = buildRequest(v);
      if (isEdit && id) {
        await api.update(id, req);
        toast.success("Changes saved");
        navigate(backTo);
      } else {
        const created = await api.create(req);
        toast.success("Recurring invoice created");
        navigate(`/manage/recurring-invoices/${created.id}`, { replace: true, state: backOrigin(backTo) });
      }
    } catch (e) {
      toastApiError(e, "Save failed.");
    }
  }

  function onInvalid() {
    toast.error("Please fix the highlighted fields before saving.");
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus({ preventScroll: true });
    });
  }

  async function onDelete() {
    if (!id) return;
    if (!(await confirm({ description: "Delete this recurring-invoice profile? Generated invoices stay.", confirmLabel: "Delete", destructive: true }))) return;
    try {
      await api.remove(id);
      navigate(backTo);
    } catch (e) {
      toastApiError(e, "Delete failed.");
    }
  }

  const saving = form.formState.isSubmitting;
  useUnsavedChangesWarning(form.formState.isDirty);
  const wItems = form.watch("items");

  return (
    <>
      <div className="mb-4">
        <Link to={backTo} className="text-sm text-muted-foreground hover:underline">
          ← Back to recurring invoices
        </Link>
      </div>

      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            {isEdit ? "Edit profile" : "New recurring invoice"}
          </h1>
          {loaded && (
            <p className="text-sm text-muted-foreground mt-1">
              {loaded.runsCompleted} run{loaded.runsCompleted === 1 ? "" : "s"} so far •
              {" "}Next: {formatDateTime(loaded.nextRunAt)}
              {loaded.lastRunAt && <> • Last: {formatDateTime(loaded.lastRunAt)}</>}
            </p>
          )}
        </div>
        {isEdit && (
          <Button type="button" variant="destructive" onClick={onDelete}>
            Delete profile
          </Button>
        )}
      </header>

      {notFound ? (
        <Notice tone="info">Profile not found.</Notice>
      ) : loading ? (
        <Loading>Loading…</Loading>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onValid, onInvalid)} className="space-y-6">
            <Panel title="Profile">
              <Grid>
                <TextField control={form.control} name="profileName" label="Profile name" required />
                <SelectField control={form.control} name="status" label="Status" options={RECURRING_STATUSES.map((s) => [s, s])} />
                <SelectField control={form.control} name="frequency" label="Frequency" options={RECURRING_FREQUENCIES.map((f) => [f, f])} />
                <NumberField control={form.control} name="interval" label="Interval (every N units)" min={0} step={1} />
                <DateField control={form.control} name="startDate" label="Start date" required />
                <DateField control={form.control} name="endDate" label="End date (blank = never)" />
                <TextField control={form.control} name="emailSubject" label="Email subject" />
                <TextField control={form.control} name="orderNumber" label="Order number" />
              </Grid>
            </Panel>

            <Panel title="Customer">
              <Grid>
                <TextField control={form.control} name="customerName" label="Name" required />
                <TextField control={form.control} name="customerCompany" label="Company" />
                <TextField control={form.control} name="customerEmail" label="Email" />
                <TextField control={form.control} name="customerPhone" label="Phone" />
                <TextField control={form.control} name="customerAddress" label="Address" className="md:col-span-2" />
              </Grid>
            </Panel>

            <Panel title="Line items">
              <div className="space-y-2">
                <div className="hidden md:flex gap-2 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <div className="flex-1">Description</div>
                  <div className="w-28 text-right">Unit price</div>
                  <div className="w-20 text-right">Qty</div>
                  <div className="w-8" />
                </div>
                {fields.map((f, i) => (
                  <div key={f.id} className="flex flex-wrap md:flex-nowrap items-center gap-2">
                    <Input
                      {...form.register(`items.${i}.description`)}
                      placeholder="Description"
                      className="flex-1 min-w-[160px]"
                    />
                    <Controller control={form.control} name={`items.${i}.unitPrice`} render={({ field }) => (
                      <NumberCell value={field.value} onChange={field.onChange} placeholder="0.00" ariaLabel="Unit price" className="w-28 text-right" />
                    )} />
                    <Controller control={form.control} name={`items.${i}.quantity`} render={({ field }) => (
                      <NumberCell value={field.value} onChange={field.onChange} integer ariaLabel="Quantity" className="w-20 text-right" />
                    )} />
                    <button
                      type="button"
                      onClick={() => wItems.length > 1 && remove(i)}
                      className="w-8 text-destructive text-lg leading-none disabled:text-slate-300"
                      disabled={wItems.length === 1}
                      aria-label="Remove line"
                    >×</button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="mt-1" onClick={() => append({ ...EMPTY })}>
                  Add line
                </Button>
              </div>
            </Panel>

            <Panel title="Pricing">
              <Grid>
                <NumberField control={form.control} name="discountPct" label="Discount" suffix="%" min={0} max={100} />
                <NumberField control={form.control} name="discountAmount" label="Discount" prefix="$" min={0} />
                <NumberField control={form.control} name="taxPct" label="Tax rate" suffix="%" min={0} max={100} />
                <NumberField control={form.control} name="shippingCharge" label="Shipping" prefix="$" min={0} />
              </Grid>
              <div className="mt-3">
                <CheckField control={form.control} name="discountAfterTax" label="Apply discount after tax" />
              </div>
            </Panel>

            <Panel title="Notes">
              <TextAreaField control={form.control} name="notes" label="" rows={3} />
            </Panel>

            {error && <Notice tone="error">{error}</Notice>}

            <div className="flex items-center justify-end gap-3">
              <Button asChild variant="ghost">
                <Link to={backTo}>Cancel</Link>
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving…" : isEdit ? "Save changes" : "Create profile"}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </>
  );
}

function toNum(s: string): number {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}


function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">{children}</CardContent>
    </Card>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{children}</div>;
}
